import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { ProductSectorMappingDetail, ProductSectorMappingDraftDetail } from 'api'
import {
  createActionsRenderer,
  createGroupedActionRenderer,
  dateFilterParams,
  gridOptionsBase,
  StatusCellRenderer,
  TypedValueGetterParams,
  withAllContext
} from 'view/components/datagrid'
import { Locale, Translation } from 'locale'
import { SectorMappingActionsButton } from './SectorMappingActionsButton'
import { compact } from 'lodash'
import { booleanFormatter, dateQuickFilterTextGetter, makeDateAndTimeFormatter } from 'view/shared/datagrid'
import { ProductSectorMappingObject } from 'api/local'
import { getDraftMappingStatusColor } from '../utils'
import { BASE_ASSET_TYPE } from 'view/shared/datagrid/baseColumns'

export enum SectorMappingGridColumnId {
  SECTOR_MAPPING_ACTIONS = 'SECTOR_MAPPING_ACTIONS',
  SECTOR_MAPPING_STATUS = 'SECTOR_MAPPING_STATUS',
  SECTOR_MAPPING_RULE_NAME = 'SECTOR_MAPPING_RULE_NAME',
  SECTOR_MAPPING_SECTOR = 'SECTOR_MAPPING_SECTOR',
  SECTOR_MAPPING_INCORPORATION_COUNTRY = 'SECTOR_MAPPING_INCORPORATION_COUNTRY',
  SECTOR_MAPPING_ISSUANCE_COUNTRY = 'SECTOR_MAPPING_ISSUANCE_COUNTRY',
  SECTOR_MAPPING_DOMICILE = 'SECTOR_MAPPING_DOMICILE',
  SECTOR_MAPPING_CURRENCY = 'SECTOR_MAPPING_CURRENCY',
  SECTOR_MAPPING_NOT_CURRENCY = 'SECTOR_MAPPING_NOT_CURRENCY',
  SECTOR_MAPPING_BTQ_ASSET_TYPE = 'SECTOR_MAPPING_BTQ_ASSET_TYPE',
  SECTOR_MAPPING_REFINITIV_ASSET_TYPE = 'SECTOR_MAPPING_REFINITIV_ASSET_TYPE',
  SECTOR_MAPPING_ASSET_SUB_TYPE = 'SECTOR_MAPPING_ASSET_SUB_TYPE',
  SECTOR_MAPPING_DEBT_TYPE = 'SECTOR_MAPPING_DEBT_TYPE',
  SECTOR_MAPPING_GOVERNMENT_BOND_TYPE = 'SECTOR_MAPPING_GOVERNMENT_BOND_TYPE',
  SECTOR_MAPPING_NOT_GOVERNMENT_BOND_TYPE = 'SECTOR_MAPPING_NOT_GOVERNMENT_BOND_TYPE',
  SECTOR_MAPPING_ISSUER_LEI = 'SECTOR_MAPPING_ISSUER_LEI',
  SECTOR_MAPPING_NOT_ISSUER_LEI = 'SECTOR_MAPPING_NOT_ISSUER_LEI',
  SECTOR_MAPPING_BASE_INDEX = 'SECTOR_MAPPING_BASE_INDEX',
  SECTOR_MAPPING_HAS_CUSIP = 'SECTOR_MAPPING_HAS_CUSIP',
  SECTOR_MAPPING_PRODUCT = 'SECTOR_MAPPING_PRODUCT',
  SECTOR_MAPPING_RULE_ID = 'SECTOR_MAPPING_RULE_ID',
  SECTOR_LAST_UPDATED_AT = 'SECTOR_LAST_UPDATED_AT',
  SECTOR_LAST_UPDATED_BY = 'SECTOR_LAST_UPDATED_BY'
}

export const SectorMappingColumnDefs = (showActions: boolean): Array<ColGroupDef | ColDef> => {
  return compact([
    showActions && {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_ACTIONS,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      equals: () => false,
      width: 180,
      minWidth: 180,
      headerComponent: withAllContext(createGroupedActionRenderer(SectorMappingActionsButton)),
      cellClass: 'action-cell',
      cellRenderer: withAllContext(createActionsRenderer(SectorMappingActionsButton)),
      pinned: 'left',
      lockPinned: true,
      enableRowGroup: false,
      lockVisible: true,
      filter: false,
      sortable: false
    },

    showActions && {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_STATUS,
      headerName: Locale.translate(Translation.TC_STATUS),
      equals: () => false,
      width: 180,
      minWidth: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDraftDetail>) => params.data?.status,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: StatusCellRenderer,
      cellRendererParams: {
        getStatusColor: (data: ProductSectorMappingDraftDetail) => getDraftMappingStatusColor(data.status)
      }
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_RULE_NAME,
      headerName: Locale.translate(Translation.TC_SECTOR_MAPPING_RULE_NAME),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.ruleName,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_SECTOR,
      headerName: Locale.translate(Translation.TC_SECTOR),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingObject>) => params.data?.productSectorName,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_PRODUCT,
      headerName: Locale.translate(Translation.TC_PRODUCT),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingObject>) => params.data?.productName,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_INCORPORATION_COUNTRY,
      headerName: Locale.translate(Translation.TC_COUNTRY_OF_INCORPORATION),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.countryOfIncorporation,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_INCORPORATION_COUNTRY,
      headerName: Locale.translate(Translation.TC_NOT_COUNTRY_OF_INCORPORATION),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) =>
        params.data?.productSectorMapping.notCountryOfIncorporation,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_ISSUANCE_COUNTRY,
      headerName: Locale.translate(Translation.TC_COUNTRY_OF_ISSUANCE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.countryOfIssuance,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_ISSUANCE_COUNTRY,
      headerName: Locale.translate(Translation.TC_NOT_COUNTRY_OF_ISSUANCE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.notCountryOfIssuance,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_DOMICILE,
      headerName: Locale.translate(Translation.TC_DOMICILE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.domicile,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_CURRENCY,
      headerName: Locale.translate(Translation.TC_CURRENCY),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.currency,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_NOT_CURRENCY,
      headerName: Locale.translate(Translation.TC_NOT_CURRENCY),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.notCurrency,
      filter: 'agSetColumnFilter'
    },
    {
      ...BASE_ASSET_TYPE,
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_BTQ_ASSET_TYPE,
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.assetType
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_REFINITIV_ASSET_TYPE,
      headerName: Locale.translate(Translation.TC_REFINITIV_ASSET_TYPE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.refinitivAssetType,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_ASSET_SUB_TYPE,
      headerName: Locale.translate(Translation.TC_ASSET_SUB_TYPE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.assetSubType,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_DEBT_TYPE,
      headerName: Locale.translate(Translation.TC_SECTOR_MAPPING_DEBT_TYPE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.debtType,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_GOVERNMENT_BOND_TYPE,
      headerName: Locale.translate(Translation.TC_SECTOR_MAPPING_GOVERNMENT_BOND_TYPE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.governmentBondType,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_NOT_GOVERNMENT_BOND_TYPE,
      headerName: Locale.translate(Translation.TC_SECTOR_MAPPING_NOT_GOVERNMENT_BOND_TYPE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.notGovernmentBondType,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_ISSUER_LEI,
      headerName: Locale.translate(Translation.TC_ISSUER_LEI),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.issuerLei,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_NOT_ISSUER_LEI,
      headerName: Locale.translate(Translation.TC_NOT_ISSUER_LEI),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.notIssuerLei,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_BASE_INDEX,
      headerName: Locale.translate(Translation.TC_BASE_INDEX_VALUE),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.baseIndexNull,
      filter: 'agSetColumnFilter',
      valueFormatter: booleanFormatter
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_HAS_CUSIP,
      headerName: Locale.translate(Translation.TC_SECTOR_MAPPING_HAS_CUSIP),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.productSectorMapping.hasCUSIP,
      filter: 'agSetColumnFilter',
      valueFormatter: booleanFormatter
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_MAPPING_RULE_ID,
      headerName: Locale.translate(Translation.TC_SECTOR_MAPPING_RULE_ID),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.id,
      filter: 'agSetColumnFilter'
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_LAST_UPDATED_BY,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.lastUpdatedAt,
      headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE_AND_TIME),
      width: 130,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      valueFormatter: makeDateAndTimeFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter
    },
    {
      colId: SectorMappingGridColumnId.SECTOR_LAST_UPDATED_BY,
      headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
      width: 180,
      valueGetter: (params: TypedValueGetterParams<ProductSectorMappingDetail>) => params.data?.lastUpdatedBy,
      filter: 'agSetColumnFilter'
    }
  ])
}

export const sectorMappingGridOptions: GridOptions = {
  ...gridOptionsBase
}
